<template>
  <v-container fluid class="consolidated-metrics-container pa-4 pa-md-6">
    <!-- Header Principal -->
    <v-row class="mb-4" align="center" justify="space-between">
      <v-col cols="12" md="8">
        <div>
          <h1 class="text-h5 font-weight-bold mb-1 header-title">
            Métricas Consolidadas por Versão
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-0">
            Análise pedagógica item a item, taxa de acerto e comportamento de distratores por versão de questão.
          </p>
        </div>
      </v-col>

      <!-- Botões de Ação do Topo -->
      <v-col cols="12" md="4" class="d-flex justify-md-end align-center gap-2 flex-wrap">
        <v-btn
          color="primary"
          variant="elevated"
          prepend-icon="mdi-refresh"
          :loading="recalculating"
          :disabled="loading || recalculating"
          @click="recalculateMetrics"
          class="text-none font-weight-bold elevation-1 mr-2"
        >
          Recalcular Métricas
        </v-btn>

        <v-btn
          color="secondary"
          variant="outlined"
          prepend-icon="mdi-download"
          :disabled="loading || filteredItems.length === 0"
          @click="exportCSV"
          class="text-none font-weight-medium"
        >
          Exportar CSV
        </v-btn>
      </v-col>
    </v-row>

    <!-- Banner de Horário da Última Atualização -->
    <v-card class="mb-6 banner-sync elevation-1 rounded-lg" color="#0B192C" dark>
      <v-card-text class="py-3 px-4 d-flex align-center justify-space-between flex-wrap gap-2">
        <div class="d-flex align-center text-white">
          <v-icon color="#93C5FD" size="20" class="mr-2">mdi-clock-check-outline</v-icon>
          <span class="font-weight-medium text-body-2">
            {{ lastUpdateText || 'Sincronização diária ativa às 03:00' }}
          </span>
        </div>
        <div class="d-flex align-center text-caption text-blue-lighten-4">
          <v-icon color="#34D399" size="14" class="mr-1">mdi-circle</v-icon>
          Base de Dados Sincronizada (Aba Metrics_By_Version)
        </div>
      </v-card-text>
    </v-card>

    <!-- Cards de KPIs (Visão Geral) -->
    <v-row class="mb-6">
      <!-- Card 1: Total de Questões -->
      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-2 rounded-xl pa-4 h-100">
          <div class="d-flex justify-space-between align-start">
            <div>
              <span class="kpi-label">Total de Questões</span>
              <h2 class="kpi-value text-slate-900 mt-1">{{ totalQuestionsCount }}</h2>
              <span class="text-caption text-medium-emphasis">Versões registradas</span>
            </div>
            <v-avatar color="#EFF6FF" size="48" class="rounded-lg">
              <v-icon color="#2563EB" size="26">mdi-format-list-numbered</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- Card 2: Total de Respostas -->
      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-2 rounded-xl pa-4 h-100">
          <div class="d-flex justify-space-between align-start">
            <div>
              <span class="kpi-label">Respostas Analisadas</span>
              <h2 class="kpi-value text-slate-900 mt-1">{{ totalResponsesFormatted }}</h2>
              <span class="text-caption text-medium-emphasis">Submissões computadas</span>
            </div>
            <v-avatar color="#ECFDF5" size="48" class="rounded-lg">
              <v-icon color="#059669" size="26">mdi-account-check-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- Card 3: Taxa Média de Acerto -->
      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-2 rounded-xl pa-4 h-100">
          <div class="d-flex justify-space-between align-start">
            <div>
              <span class="kpi-label">Taxa Média de Acerto</span>
              <h2 class="kpi-value mt-1" :style="{ color: averageAccuracyColor }">
                {{ averageAccuracyFormatted }}
              </h2>
              <span class="text-caption text-medium-emphasis">Geral de todas as questões</span>
            </div>
            <v-avatar :color="averageAccuracyBgColor" size="48" class="rounded-lg">
              <v-icon :color="averageAccuracyColor" size="26">mdi-percent-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- Card 4: Questões em Alerta (< 35% de acerto) -->
      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-2 rounded-xl pa-4 h-100">
          <div class="d-flex justify-space-between align-start">
            <div>
              <span class="kpi-label">Questões em Alerta</span>
              <h2 class="kpi-value text-red-darken-1 mt-1">{{ alertQuestionsCount }}</h2>
              <span class="text-caption text-medium-emphasis">&lt; 35% de acerto (com respostas)</span>
            </div>
            <v-avatar color="#FFF1F2" size="48" class="rounded-lg">
              <v-icon color="#DC2626" size="26">mdi-alert-circle-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Barra de Filtros e Busca -->
    <v-card class="elevation-1 rounded-xl pa-4 mb-4 filter-bar">
      <v-row align="center" dense>
        <!-- Busca Livre -->
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Buscar por ID, Tema, SubTema ou Distrator..."
            density="compact"
            variant="outlined"
            hide-details
            clearable
          ></v-text-field>
        </v-col>

        <!-- Filtro por Tema -->
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="selectedTheme"
            :items="themeOptions"
            label="Filtrar por Tema"
            density="compact"
            variant="outlined"
            hide-details
            clearable
          ></v-select>
        </v-col>

        <!-- Filtro por Dificuldade -->
        <v-col cols="6" sm="4" md="2">
          <v-select
            v-model="selectedDifficulty"
            :items="difficultyOptions"
            label="Dificuldade"
            density="compact"
            variant="outlined"
            hide-details
            clearable
          ></v-select>
        </v-col>

        <!-- Filtro por Status -->
        <v-col cols="6" sm="4" md="2">
          <v-select
            v-model="selectedStatus"
            :items="statusOptions"
            label="Status"
            density="compact"
            variant="outlined"
            hide-details
          ></v-select>
        </v-col>

        <!-- Botão Limpar Filtros -->
        <v-col cols="12" sm="4" md="1" class="text-right">
          <v-btn
            icon="mdi-filter-remove-outline"
            variant="text"
            color="grey-darken-1"
            title="Limpar Filtros"
            @click="clearFilters"
          ></v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tabela Principal de Métricas -->
    <v-card class="elevation-2 rounded-xl overflow-hidden main-table-card">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-16">
        <v-progress-circular indeterminate color="primary" size="56" width="4"></v-progress-circular>
        <div class="text-subtitle-1 font-weight-bold text-slate-700 mt-4">
          Carregando métricas consolidadas da planilha...
        </div>
        <div class="text-caption text-medium-emphasis">Processando base de questões e versões</div>
      </div>

      <!-- Tabela de Dados -->
      <v-data-table
        v-else
        :headers="headers"
        :items="filteredItems"
        item-value="id"
        class="metrics-table"
        density="comfortable"
        :items-per-page="25"
        :items-per-page-options="[15, 25, 50, 100]"
        hover
      >
        <!-- Slot ID: Alto contraste e fácil visualização -->
        <template v-slot:[`item.id`]="{ item }">
          <span class="id-badge">
            #{{ getItemProp(item, 'id') }}
          </span>
        </template>

        <!-- Slot Tema & SubTema -->
        <template v-slot:[`item.tema`]="{ item }">
          <div class="py-1">
            <div class="font-weight-bold text-slate-900 text-body-2">
              {{ getItemProp(item, 'tema') || '-' }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ getItemProp(item, 'subTema') || '-' }}
            </div>
          </div>
        </template>

        <!-- Slot Versão -->
        <template v-slot:[`item.versao`]="{ item }">
          <v-chip size="x-small" color="primary" variant="outlined" class="font-weight-bold">
            {{ getItemProp(item, 'versao') }}
          </v-chip>
        </template>

        <!-- Slot Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            size="x-small"
            :color="getItemProp(item, 'status') === 'Ativa' ? 'success' : 'grey'"
            variant="flat"
            class="font-weight-bold"
          >
            {{ getItemProp(item, 'status') }}
          </v-chip>
        </template>

        <!-- Slot Gabarito -->
        <template v-slot:[`item.gabarito`]="{ item }">
          <v-avatar size="24" color="#1E293B" class="text-white font-weight-bold text-caption">
            {{ getItemProp(item, 'gabarito') }}
          </v-avatar>
        </template>

        <!-- Slot Total Respostas -->
        <template v-slot:[`item.totalRespostas`]="{ item }">
          <span class="font-weight-medium">
            {{ Number(getItemProp(item, 'totalRespostas')).toLocaleString('pt-BR') }}
          </span>
        </template>

        <!-- Slot Taxa de Acerto com Barra Visual -->
        <template v-slot:[`item.taxaAcerto`]="{ item }">
          <div style="min-width: 110px;" class="py-1">
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="font-weight-bold text-caption" :style="{ color: getAccuracyColor(getItemProp(item, 'taxaAcerto'), getItemProp(item, 'totalRespostas')) }">
                {{ formatPercent(getItemProp(item, 'taxaAcerto')) }}
              </span>
              <span class="text-caption text-medium-emphasis" style="font-size: 10px;">
                {{ getItemProp(item, 'acertos') }}/{{ getItemProp(item, 'totalRespostas') }}
              </span>
            </div>
            <v-progress-linear
              :model-value="getItemProp(item, 'totalRespostas') > 0 ? (getItemProp(item, 'taxaAcerto') * 100) : 0"
              :color="getAccuracyColor(getItemProp(item, 'taxaAcerto'), getItemProp(item, 'totalRespostas'))"
              height="6"
              rounded
            ></v-progress-linear>
          </div>
        </template>

        <!-- Slot Classificação de Dificuldade -->
        <template v-slot:[`item.classificacao`]="{ item }">
          <span class="text-caption font-weight-medium">
            {{ getItemProp(item, 'classificacao') }}
          </span>
        </template>

        <!-- Slot Distrator Principal -->
        <template v-slot:[`item.distrator`]="{ item }">
          <v-chip
            v-if="getItemProp(item, 'distrator') && getItemProp(item, 'distrator') !== '-'"
            size="x-small"
            color="orange-darken-3"
            variant="tonal"
            class="font-weight-bold"
          >
            {{ getItemProp(item, 'distrator') }}
          </v-chip>
          <span v-else class="text-caption text-medium-emphasis">-</span>
        </template>

        <!-- Slot Ações / Ver Detalhes -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            size="x-small"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-eye-outline"
            @click="openDetailsDialog(item)"
            class="text-none font-weight-bold"
          >
            Ver Detalhes
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo Modal: Análise Detalhada, Enunciado e Distratores -->
    <v-dialog v-model="detailsDialog" max-width="850px" scrollable>
      <v-card v-if="selectedQuestion" class="rounded-xl overflow-hidden pa-0">
        <!-- Header do Modal -->
        <v-card-title class="py-4 px-6 text-white d-flex align-center justify-space-between modal-header">
          <div class="d-flex align-center">
            <span class="modal-id-pill mr-3">#{{ selectedQuestion.id }}</span>
            <div>
              <div class="text-subtitle-1 font-weight-bold">
                Questão #{{ selectedQuestion.id }} — Versão {{ selectedQuestion.versao }}
              </div>
              <div class="text-caption text-blue-lighten-4">
                Vigência: {{ selectedQuestion.vigencia }}
              </div>
            </div>
          </div>
          <v-btn icon="mdi-close" variant="text" color="white" size="small" @click="detailsDialog = false"></v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Metadados da Questão -->
          <v-row class="mb-4">
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Tema</div>
              <div class="font-weight-bold text-body-2">{{ selectedQuestion.tema }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">SubTema</div>
              <div class="font-weight-bold text-body-2">{{ selectedQuestion.subTema }}</div>
            </v-col>
          </v-row>

          <!-- Resumo de Desempenho Numérico -->
          <v-card variant="outlined" class="pa-4 rounded-lg mb-6 bg-grey-lighten-5 border">
            <v-row align="center">
              <v-col cols="4" class="text-center border-e">
                <div class="text-caption text-medium-emphasis">Total Respostas</div>
                <div class="text-h6 font-weight-bold text-slate-900">
                  {{ selectedQuestion.totalRespostas }}
                </div>
              </v-col>
              <v-col cols="4" class="text-center border-e">
                <div class="text-caption text-medium-emphasis">Acertos (Gabarito: {{ selectedQuestion.gabarito }})</div>
                <div class="text-h6 font-weight-bold text-emerald-600" style="color: #059669;">
                  {{ selectedQuestion.acertos }}
                </div>
              </v-col>
              <v-col cols="4" class="text-center">
                <div class="text-caption text-medium-emphasis">Taxa de Acerto</div>
                <div class="text-h6 font-weight-bold" :style="{ color: getAccuracyColor(selectedQuestion.taxaAcerto, selectedQuestion.totalRespostas) }">
                  {{ formatPercent(selectedQuestion.taxaAcerto) }}
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Card do Enunciado Completo da Questão -->
          <v-card variant="outlined" class="pa-4 rounded-lg mb-6 border question-enunciado-card">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="d-flex align-center">
                <v-icon size="18" color="primary" class="mr-2">mdi-text-box-outline</v-icon>
                <span class="text-caption font-weight-bold text-uppercase text-medium-emphasis">
                  Enunciado da Questão
                </span>
              </div>
              <div v-if="loadingQuestionDetail" class="d-flex align-center">
                <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2"></v-progress-circular>
                <span class="text-caption text-medium-emphasis">Carregando enunciado...</span>
              </div>
            </div>

            <div v-if="currentRawQuestion && currentRawQuestion.question" class="text-body-1 question-text">
              {{ currentRawQuestion.question }}
            </div>
            <div v-else-if="loadingQuestionDetail" class="text-caption text-medium-emphasis py-2">
              Buscando enunciado nos registros da base...
            </div>
            <div v-else class="text-caption text-medium-emphasis py-2">
              Enunciado não localizado ou questão sem texto cadastrado.
            </div>

            <!-- Imagem da Questão (se houver) -->
            <div v-if="currentRawQuestion && currentRawQuestion.questionImage" class="mt-4 text-center">
              <v-img
                :src="currentRawQuestion.questionImage"
                max-height="350"
                contain
                class="rounded-lg border bg-white mx-auto"
              ></v-img>
            </div>
          </v-card>

          <!-- Alternativas com Texto e Estatísticas de Escolha -->
          <h3 class="text-subtitle-2 font-weight-bold text-slate-800 mb-3 d-flex align-center">
            <v-icon color="primary" size="18" class="mr-2">mdi-chart-bar</v-icon>
            Alternativas e Distribuição de Respostas:
          </h3>

          <div
            v-for="opt in ['A', 'B', 'C', 'D', 'E']"
            :key="opt"
            class="mb-3 pa-3 rounded-lg border alternative-card"
            :class="{ 'gabarito-highlight': opt === selectedQuestion.gabarito }"
          >
            <div class="d-flex justify-space-between align-start mb-2">
              <div class="d-flex align-start flex-grow-1 mr-3">
                <v-avatar
                  size="24"
                  :color="opt === selectedQuestion.gabarito ? '#10B981' : '#475569'"
                  class="text-white font-weight-bold text-caption mr-2 mt-0.5 flex-shrink-0"
                >
                  {{ opt }}
                </v-avatar>
                <div>
                  <!-- Texto da Alternativa -->
                  <div class="font-weight-medium text-body-2 text-slate-800" style="white-space: pre-line;">
                    {{ getOptionText(opt) || ('Opção ' + opt) }}
                  </div>
                  <!-- Badges de Gabarito e Distrator -->
                  <div class="mt-1 d-flex gap-2">
                    <v-chip
                      v-if="opt === selectedQuestion.gabarito"
                      size="x-small"
                      color="success"
                      variant="flat"
                      class="font-weight-bold"
                    >
                      Gabarito Oficial ✓
                    </v-chip>
                    <v-chip
                      v-else-if="selectedQuestion.distrator && selectedQuestion.distrator.startsWith(opt)"
                      size="x-small"
                      color="warning"
                      variant="flat"
                      class="font-weight-bold"
                    >
                      Distrator Principal
                    </v-chip>
                  </div>
                </div>
              </div>

              <!-- Percentual Numérico -->
              <div class="text-right flex-shrink-0">
                <span class="font-weight-bold text-body-2 text-slate-900">
                  {{ formatPercent(selectedQuestion['pct' + opt]) }}
                </span>
              </div>
            </div>

            <!-- Barra Visual do Percentual -->
            <v-progress-linear
              :model-value="(selectedQuestion['pct' + opt] || 0) * 100"
              :color="opt === selectedQuestion.gabarito ? '#10B981' : (selectedQuestion.distrator && selectedQuestion.distrator.startsWith(opt) ? '#F59E0B' : '#CBD5E1')"
              height="8"
              rounded
            ></v-progress-linear>
          </div>

          <!-- Resolução Comentada (se disponível) -->
          <v-card
            v-if="currentRawQuestion && currentRawQuestion.questionComment"
            variant="outlined"
            class="pa-4 rounded-lg mt-4 border comment-card"
          >
            <div class="d-flex align-center mb-1">
              <v-icon size="18" color="#2563EB" class="mr-2">mdi-comment-check-outline</v-icon>
              <span class="text-caption font-weight-bold text-uppercase" style="color: #1E40AF;">
                Resolução Comentada
              </span>
            </div>
            <div class="text-body-2 comment-text">
              {{ currentRawQuestion.questionComment }}
            </div>
          </v-card>
        </v-card-text>

        <v-card-actions class="pa-4 bg-grey-lighten-4 justify-end">
          <v-btn color="primary" variant="elevated" class="text-none font-weight-bold px-6" @click="detailsDialog = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';

const TABLE_URL = 'https://script.google.com/macros/s/AKfycbz2xew0Ie2dSXvXLXToc5oHE0icdWUdAoM9_YPlk_crczQ-IX3YJSfHT0RYPaMT9q1aaA/exec';

export default {
  name: 'ConsolidatedMetrics',
  data() {
    return {
      loading: true,
      recalculating: false,
      lastUpdateText: '',
      items: [],
      search: '',
      selectedTheme: null,
      selectedDifficulty: null,
      selectedStatus: 'Todas',
      detailsDialog: false,
      selectedQuestion: null,
      currentRawQuestion: null,
      loadingQuestionDetail: false,

      statusOptions: ['Todas', 'Ativa', 'Histórica'],
      difficultyOptions: [
        'Todas',
        'Muito Fácil',
        'Fácil',
        'Média',
        'Difícil',
        'Muito Difícil',
        'Sem dados'
      ],

      headers: [
        { title: 'ID', key: 'id', align: 'center', width: '85px', sortable: true },
        { title: 'Tema e SubTema', key: 'tema', align: 'start', sortable: true },
        { title: 'Versão', key: 'versao', align: 'center', width: '85px', sortable: true },
        { title: 'Status', key: 'status', align: 'center', width: '90px', sortable: true },
        { title: 'Gabarito', key: 'gabarito', align: 'center', width: '75px', sortable: true },
        { title: 'Total', key: 'totalRespostas', align: 'end', width: '100px', sortable: true },
        { title: 'Taxa de Acerto', key: 'taxaAcerto', align: 'center', width: '140px', sortable: true },
        { title: 'Classificação', key: 'classificacao', align: 'center', width: '125px', sortable: true },
        { title: 'Distrator Principal', key: 'distrator', align: 'center', width: '145px', sortable: true },
        { title: 'Ações', key: 'actions', align: 'center', width: '120px', sortable: false }
      ]
    };
  },

  computed: {
    loadedRawQuestions() {
      return this.$store.getters.loadedRawQuestions || [];
    },

    // Opções únicas de tema para o dropdown
    themeOptions() {
      const themes = new Set();
      this.items.forEach(it => {
        if (it.tema) themes.add(it.tema);
      });
      return ['Todos', ...Array.from(themes).sort()];
    },

    // Itens filtrados dinamicamente
    filteredItems() {
      let result = [...this.items];

      // Filtro de Busca Livre
      if (this.search && this.search.trim() !== '') {
        const query = this.search.toLowerCase().trim();
        result = result.filter(it => {
          const idMatch = String(it.id).includes(query);
          const temaMatch = it.tema && it.tema.toLowerCase().includes(query);
          const subTemaMatch = it.subTema && it.subTema.toLowerCase().includes(query);
          const distratorMatch = it.distrator && it.distrator.toLowerCase().includes(query);
          return idMatch || temaMatch || subTemaMatch || distratorMatch;
        });
      }

      // Filtro de Tema
      if (this.selectedTheme && this.selectedTheme !== 'Todos') {
        result = result.filter(it => it.tema === this.selectedTheme);
      }

      // Filtro de Dificuldade
      if (this.selectedDifficulty && this.selectedDifficulty !== 'Todas') {
        result = result.filter(it => {
          if (!it.classificacao) return false;
          return it.classificacao.includes(this.selectedDifficulty);
        });
      }

      // Filtro de Status
      if (this.selectedStatus && this.selectedStatus !== 'Todas') {
        result = result.filter(it => it.status === this.selectedStatus);
      }

      return result;
    },

    totalQuestionsCount() {
      return this.items.length.toLocaleString('pt-BR');
    },

    totalResponsesFormatted() {
      const sum = this.items.reduce((acc, it) => acc + (Number(it.totalRespostas) || 0), 0);
      return sum.toLocaleString('pt-BR');
    },

    averageAccuracy() {
      let totalResponses = 0;
      let totalRight = 0;
      this.items.forEach(it => {
        totalResponses += Number(it.totalRespostas) || 0;
        totalRight += Number(it.acertos) || 0;
      });
      if (totalResponses === 0) return 0;
      return totalRight / totalResponses;
    },

    averageAccuracyFormatted() {
      return (this.averageAccuracy * 100).toFixed(1) + '%';
    },

    averageAccuracyColor() {
      if (this.averageAccuracy >= 0.70) return '#059669';
      if (this.averageAccuracy >= 0.40) return '#D97706';
      return '#DC2626';
    },

    averageAccuracyBgColor() {
      if (this.averageAccuracy >= 0.70) return '#ECFDF5';
      if (this.averageAccuracy >= 0.40) return '#FEF3C7';
      return '#FFF1F2';
    },

    alertQuestionsCount() {
      return this.items.filter(it => it.totalRespostas > 0 && it.taxaAcerto < 0.35).length;
    }
  },

  watch: {
    loadedRawQuestions() {
      if (this.detailsDialog && this.selectedQuestion && !this.currentRawQuestion) {
        this.matchRawQuestion();
      }
    }
  },

  mounted() {
    this.fetchConsolidatedMetrics();
    if (!this.$store.getters.loadedRawQuestions || this.$store.getters.loadedRawQuestions.length === 0) {
      this.$store.dispatch('loadRawQuestions');
    }
  },

  methods: {
    // Helper seguro para obter propriedades do item em diferentes versões do Vuetify
    getItemProp(item, prop) {
      if (!item) return '';
      if (item[prop] !== undefined) return item[prop];
      if (item.raw && item.raw[prop] !== undefined) return item.raw[prop];
      return '';
    },

    formatPercent(val) {
      if (val === undefined || val === null || isNaN(val)) return '0.0%';
      return (Number(val) * 100).toFixed(1) + '%';
    },

    getAccuracyColor(acc, total) {
      if (!total || total === 0) return '#94A3B8';
      const num = Number(acc) || 0;
      if (num >= 0.70) return '#10B981'; // Verde
      if (num >= 0.40) return '#F59E0B'; // Âmbar
      return '#EF4444'; // Vermelho
    },

    clearFilters() {
      this.search = '';
      this.selectedTheme = null;
      this.selectedDifficulty = null;
      this.selectedStatus = 'Todas';
    },

    async openDetailsDialog(item) {
      this.selectedQuestion = item.raw || item;
      this.currentRawQuestion = null;
      this.detailsDialog = true;

      // Se as questões brutas ainda não foram carregadas, busca no store
      if (!this.loadedRawQuestions || this.loadedRawQuestions.length === 0) {
        this.loadingQuestionDetail = true;
        try {
          await this.$store.dispatch('loadRawQuestions');
        } catch (e) {
          console.error('Erro ao carregar detalhes da questão:', e);
        } finally {
          this.loadingQuestionDetail = false;
        }
      }

      this.matchRawQuestion();
    },

    matchRawQuestion() {
      if (!this.selectedQuestion) return;
      const qId = String(this.selectedQuestion.id).trim();
      const rawList = this.loadedRawQuestions || [];
      const found = rawList.find(rq => String(rq.questionId).trim() === qId);
      this.currentRawQuestion = found || null;
    },

    getOptionText(opt) {
      if (!this.currentRawQuestion || !this.currentRawQuestion.questionOptions) return '';
      const map = { A: 0, B: 1, C: 2, D: 3, E: 4 };
      const idx = map[opt];
      return this.currentRawQuestion.questionOptions[idx] || '';
    },

    // Carrega dados da aba Metrics_By_Version via Apps Script
    async fetchConsolidatedMetrics() {
      this.loading = true;
      try {
        const response = await axios.get(TABLE_URL, {
          params: { action: 'getConsolidatedMetrics' }
        });

        if (response.data && response.data.items) {
          this.items = response.data.items;
          this.lastUpdateText = response.data.lastUpdate || '';
        }
      } catch (err) {
        console.error('Erro ao carregar métricas consolidadas:', err);
      } finally {
        this.loading = false;
      }
    },

    // Dispara a recomputação completa das métricas na planilha sob demanda
    async recalculateMetrics() {
      this.recalculating = true;
      try {
        const response = await axios.get(TABLE_URL, {
          params: { action: 'consolidateMetrics' }
        });
        if (response.data && response.data.success) {
          await this.fetchConsolidatedMetrics();
        }
      } catch (err) {
        console.error('Erro ao recalcular métricas:', err);
      } finally {
        this.recalculating = false;
      }
    },

    // Exporta os dados atualmente filtrados para um arquivo CSV
    exportCSV() {
      const rows = this.filteredItems;
      if (rows.length === 0) return;

      const headers = [
        'ID', 'Tema', 'SubTema', 'Versao', 'Status', 'Vigencia',
        'Gabarito', 'Total_Respostas', 'Acertos', 'Erros',
        'Taxa_Acerto', 'Classificacao', 'Distrator_Principal',
        'Pct_A', 'Pct_B', 'Pct_C', 'Pct_D', 'Pct_E'
      ];

      const csvContent = [];
      csvContent.push(headers.join(';'));

      rows.forEach(it => {
        const row = [
          it.id,
          `"${(it.tema || '').replace(/"/g, '""')}"`,
          `"${(it.subTema || '').replace(/"/g, '""')}"`,
          it.versao,
          it.status,
          `"${it.vigencia || ''}"`,
          it.gabarito,
          it.totalRespostas,
          it.acertos,
          it.erros,
          (it.taxaAcerto * 100).toFixed(1) + '%',
          `"${it.classificacao || ''}"`,
          `"${it.distrator || ''}"`,
          (it.pctA * 100).toFixed(1) + '%',
          (it.pctB * 100).toFixed(1) + '%',
          (it.pctC * 100).toFixed(1) + '%',
          (it.pctD * 100).toFixed(1) + '%',
          (it.pctE * 100).toFixed(1) + '%'
        ];
        csvContent.push(row.join(';'));
      });

      // Adiciona BOM UTF-8 para compatibilidade perfeita com Excel
      const blob = new Blob(['\uFEFF' + csvContent.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `metricas_consolidadas_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
.consolidated-metrics-container {
  max-width: 1600px;
  margin: 0 auto;
  font-family: var(--vs-font-body), "Inter", sans-serif !important;
}

.header-title {
  font-family: var(--vs-font-display), "Roboto Condensed", sans-serif !important;
  color: var(--vs-navy-deep, #05122e);
  letter-spacing: -0.02em;
}

.kpi-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #E2E8F0;
  font-family: var(--vs-font-body), sans-serif;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08) !important;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: var(--vs-font-body), sans-serif;
}

.kpi-value {
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1.2;
  font-family: var(--vs-font-display), sans-serif;
}

.banner-sync {
  background: linear-gradient(135deg, #0B192C 0%, #1E3A8A 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-family: var(--vs-font-body), sans-serif;
}

.filter-bar {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  font-family: var(--vs-font-body), sans-serif;
}

.main-table-card {
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  font-family: var(--vs-font-body), sans-serif;
}

/* Badge de ID nítido e com alto contraste */
.id-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  padding: 3px 8px;
  background-color: #0F172A !important;
  color: #FFFFFF !important;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.82rem;
  font-family: var(--vs-font-body), monospace;
  letter-spacing: 0.02em;
}

/* Custom Scroll e Tabela */
.metrics-table :deep(th) {
  background-color: #F8FAFC !important;
  color: #1E293B !important;
  font-weight: 700 !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 2px solid #E2E8F0 !important;
  font-family: var(--vs-font-body), sans-serif;
}

.metrics-table :deep(tr:hover) {
  background-color: #F1F5F9 !important;
}

.metrics-table :deep(td) {
  border-bottom: 1px solid #F1F5F9 !important;
  font-family: var(--vs-font-body), sans-serif;
}

/* Estilos do Modal de Detalhes */
.modal-header {
  background: linear-gradient(135deg, #0B192C 0%, #1E3A8A 100%);
  font-family: var(--vs-font-display), sans-serif;
}

.modal-id-pill {
  display: inline-block;
  padding: 2px 10px;
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
  border-radius: 6px;
  font-weight: 800;
  font-size: 0.85rem;
}

.question-enunciado-card {
  background-color: #F8FAFC;
  border-color: #E2E8F0 !important;
}

.question-text {
  color: #0F172A;
  line-height: 1.65;
  white-space: pre-line;
  font-family: var(--vs-font-body), sans-serif;
}

.alternative-card {
  background-color: #FFFFFF;
  border: 1px solid #E2E8F0;
  transition: background-color 0.15s ease;
}

.alternative-card.gabarito-highlight {
  background-color: #F0FDF4;
  border-color: #86EFAC !important;
}

.comment-card {
  background-color: #EFF6FF;
  border-color: #BFDBFE !important;
}

.comment-text {
  color: #1E293B;
  line-height: 1.55;
  white-space: pre-line;
  font-family: var(--vs-font-body), sans-serif;
}
</style>
